import React from 'react';
import {
  Box,
  VStack,
  Text,
  Heading,
  Button,
  ButtonText,
} from '@gluestack-ui/themed';
import { CheckCircle, XCircle } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { MCQAttemptResult } from './types';

interface QuizResultsProps {
  result: MCQAttemptResult;
  actionLabel?: string;
  onAction?: () => void;
  showAction?: boolean;
}

export const QuizResults: React.FC<QuizResultsProps> = ({
  result,
  actionLabel,
  onAction,
  showAction = false,
}) => {
  const { t } = useTranslation();
  const percentage = Math.round((result.score / result.maxScore) * 100);

  return (
    <Box
      bg={result.passed ? '$success50' : '$error50'}
      borderRadius="$lg"
      p="$6"
      mb="$4"
      borderWidth={2}
      borderColor={result.passed ? '$success500' : '$error500'}
    >
      <VStack space="lg" alignItems="center">
        {/* Icon */}
        {result.passed ? (
          <CheckCircle size={64} color="$success600" />
        ) : (
          <XCircle size={64} color="$error600" />
        )}

        {/* Status */}
        <Heading size="xl" textAlign="center">
          {result.passed ? t('student.mcq.passed') : t('student.mcq.failed')}
        </Heading>

        {/* Score */}
        <VStack space="sm" alignItems="center">
          <Text size="lg" bold>
            {t('student.mcq.yourScore')}
          </Text>
          <Heading size="2xl">
            {result.score}
            {' / '}
            {result.maxScore}
          </Heading>
          <Text size="lg" color="$textSubtle">
            {percentage}
            %
          </Text>
        </VStack>

        {/* Action Button */}
        {showAction && onAction && (
          <Button
            onPress={onAction}
            variant="solid"
            action={result.passed ? 'positive' : 'negative'}
            size="lg"
          >
            <ButtonText>{actionLabel || t('student.mcq.tryAgain')}</ButtonText>
          </Button>
        )}

        {/* Message */}
        {!result.passed && (
          <Text size="sm" color="$textSubtle" textAlign="center">
            Review the questions below to see where you went wrong.
          </Text>
        )}
        {result.passed && (
          <Text size="sm" color="$textSubtle" textAlign="center">
            Great job! You have passed this quiz.
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default QuizResults;
